import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { client, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Leaf, Send, Trash2, Sparkles } from "lucide-react";

const SUGGESTIONS = [
  "I'm having a strong craving right now.",
  "I feel like I'm going to relapse.",
  "Help me pray through this moment.",
  "I made it through today — thank you.",
];

function renderInline(text) {
  const nodes = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*\n]+\*|_[^_\n]+_)/g;
  let last = 0, m, key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) nodes.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    else nodes.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function renderContent(text) {
  // Convert lightweight markdown (headings, bold/italic, bullets) and lift scripture into serif blockquotes
  return text.split("\n").map((line, i) => {
    let trimmed = line.trim();
    if (!trimmed) return <div key={i} className="h-2" />;
    const heading = /^#{1,6}\s+/.test(trimmed);
    trimmed = trimmed.replace(/^#{1,6}\s+/, "");
    const blockquote = /^>\s?/.test(trimmed);
    trimmed = trimmed.replace(/^>\s?/, "");
    const bullet = /^[-*]\s+/.test(trimmed);
    trimmed = trimmed.replace(/^[-*]\s+/, "");
    const isQuote = blockquote || /^["“]/.test(trimmed) || (/\b\d?\s?[A-Z][a-z]+\s\d+:\d+/.test(trimmed) && trimmed.length < 160 && /["“]/.test(trimmed));
    if (isQuote) {
      return <p key={i} className="font-scripture italic text-scripture text-olive border-l-2 border-gold pl-3 my-2">{renderInline(trimmed)}</p>;
    }
    if (heading) {
      return <p key={i} className="font-serif text-olive font-semibold mt-1 mb-1.5">{renderInline(trimmed)}</p>;
    }
    if (bullet) {
      return <p key={i} className="mb-1.5 pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-terracotta">{renderInline(trimmed)}</p>;
    }
    return <p key={i} className="mb-2 last:mb-0">{renderInline(trimmed)}</p>;
  });
}

export default function CoachPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [upsell, setUpsell] = useState(false);
  const scrollRef = useRef(null);
  const startedRef = useRef(false);
  const isMember = !!user?.membership?.is_member;

  useEffect(() => {
    client.get("/coach/history").then((r) => {
      if (!startedRef.current) setMessages(r.data);
    }).finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    startedRef.current = true;
    setInput("");
    setUpsell(false);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const resp = await fetch(`${API}/coach/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg }),
      });
      if (!resp.ok || !resp.body) throw new Error("no stream");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              if (!last || last.role !== "assistant") return m;
              copy[copy.length - 1] = { role: "assistant", content: last.content + payload.delta };
              return copy;
            });
          } else if (payload.error) {
            throw new Error(payload.error);
          } else if (payload.limit && payload.reason === "allowance" && !isMember) {
            setUpsell(true);
          }
        }
      }
    } catch (e) {
      toast.error("Companion couldn't respond just now. Please try again.");
      setMessages((m) => {
        const copy = [...m];
        if (copy[copy.length - 1]?.role === "assistant" && !copy[copy.length - 1].content) {
          copy[copy.length - 1] = { role: "assistant", content: "I'm sorry — I couldn't respond just now. But God is still with you in this moment. Please try again." };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const clear = async () => {
    await client.delete("/coach/history");
    setMessages([]);
    toast.success("Conversation cleared.");
  };

  useEffect(() => {
    if (!loaded) return;
    const seed = sessionStorage.getItem("companion_seed");
    if (seed) {
      sessionStorage.removeItem("companion_seed");
      send(seed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  return (
    <div className="flex flex-col h-[calc(100vh-9rem)] lg:h-[calc(100vh-4rem)]" data-testid="coach-page">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="rounded-2xl bg-sky p-2.5"><Leaf className="w-5 h-5 text-sand" /></div>
          <div>
            <h1 className="font-serif text-3xl text-olive leading-none">Companion</h1>
            <p className="text-xs text-stone mt-1">A faith-based companion for the journey · here whenever you need</p>
          </div>
        </div>
        {messages.length > 0 && (
          <button data-testid="clear-chat" onClick={clear} className="rounded-full p-2 text-stone hover:bg-sage hover:text-terracotta transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto rounded-3xl bg-card border border-border p-5 sm:p-6 shadow-soft space-y-4">
        {loaded && messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="rounded-full bg-sage p-5 mb-5"><Sparkles className="w-7 h-7 text-sky" /></div>
            <p className="font-serif text-2xl text-olive mb-2">Peace be with you.</p>
            <p className="text-olive/70 max-w-md mb-6">
              I'm here to walk with you — through cravings, hard nights, and small victories. Share whatever is on your heart.
            </p>
            <div className="grid sm:grid-cols-2 gap-2.5 w-full max-w-md">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid="suggestion" onClick={() => send(s)} className="rounded-2xl border border-border p-3 text-sm text-olive text-left transition-colors hover:bg-sage">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-3xl px-5 py-3.5 leading-relaxed ${m.role === "user" ? "bg-brand-teal text-brand-navy rounded-br-md" : "bg-sage text-olive rounded-bl-md"}`} data-testid={`msg-${m.role}`}>
              {m.role === "assistant" ? (
                <div>{m.content ? renderContent(m.content) : <span className="text-stone">…</span>}</div>
              ) : (
                m.content
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {upsell && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className="mt-4 rounded-2xl bg-brand-navy text-brand-ivory p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="companion-upsell">
          <div className="flex-1">
            <p className="font-serif text-lg leading-snug">Want more room to talk it through?</p>
            <p className="text-sm text-brand-ivory-muted">Members get about 4× the daily conversations — and it can be covered for you.</p>
          </div>
          <Button data-testid="companion-upsell-btn" onClick={() => navigate("/app/membership")}
            className="rounded-full bg-brand-teal hover:bg-brand-teal/90 text-brand-navy font-medium gap-2 shrink-0">
            <Sparkles className="w-4 h-4" /> Continue your journey
          </Button>
        </motion.div>
      )}

      <div className="mt-4 flex items-end gap-3">
        <textarea
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={1}
          placeholder="Share what's on your heart…"
          className="flex-1 resize-none rounded-2xl border border-border bg-card px-5 py-3.5 text-olive outline-none focus:ring-2 focus:ring-sky/40 max-h-32"
        />
        <Button data-testid="send-btn" onClick={() => send()} disabled={streaming || !input.trim()} className="rounded-2xl bg-terracotta hover:bg-terracotta-soft text-sand h-[52px] w-[52px] p-0 shrink-0">
          <Send className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}
